// src/hooks/usePivot.js
import { useState, useCallback } from 'react';
import { apiService } from '../services/api';

export const usePivot = () => {
  const [pivotData, setPivotData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pivotType, setPivotType] = useState(null);

  const pivotByIconHash = useCallback(async (iconHash, limit = 100) => {
    try {
      setLoading(true);
      setError(null);
      setPivotType('icon_hash');

      const response = await fetch(
        `${apiService.baseUrl}/pivots/icon-hash/${encodeURIComponent(iconHash)}?limit=${limit}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to pivot: ${response.status}`);
      }

      const data = await response.json();
      setPivotData(data);
      return data;
    } catch (err) {
      console.error('Pivot error (icon_hash):', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const pivotByCertificate = useCallback(async (certSha256, limit = 100) => {
    try {
      setLoading(true);
      setError(null);
      setPivotType('certificate');

      const response = await fetch(
        `${apiService.baseUrl}/pivots/certificate/${encodeURIComponent(certSha256)}?limit=${limit}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to pivot: ${response.status}`);
      }

      const data = await response.json();
      setPivotData(data);
      return data;
    } catch (err) {
      console.error('Pivot error (certificate):', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const pivotByPermission = useCallback(async (permission, limit = 100) => {
    try {
      setLoading(true);
      setError(null);
      setPivotType('permission');

      const response = await fetch(
        `${apiService.baseUrl}/pivots/permission/${encodeURIComponent(permission)}?limit=${limit}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to pivot: ${response.status}`);
      }

      const data = await response.json();
      setPivotData(data);
      return data;
    } catch (err) {
      console.error('Pivot error (permission):', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const pivotByPermHash = useCallback(async (permHash, limit = 100) => {
    try {
      setLoading(true);
      setError(null);
      setPivotType('perm_hash');

      const response = await fetch(
        `${apiService.baseUrl}/pivots/perm-hash/${encodeURIComponent(permHash)}?limit=${limit}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to pivot: ${response.status}`);
      }

      const data = await response.json();
      setPivotData(data);
      return data;
    } catch (err) {
      console.error('Pivot error (perm_hash):', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const pivotByMalwareFamily = useCallback(async (malwareFamily, limit = 100) => {
    try {
      setLoading(true);
      setError(null);
      setPivotType('malware_family');

      const response = await fetch(
        `${apiService.baseUrl}/pivots/malware-family/${encodeURIComponent(malwareFamily)}?limit=${limit}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to pivot: ${response.status}`);
      }

      const data = await response.json();
      setPivotData(data);
      return data;
    } catch (err) {
      console.error('Pivot error (malware_family):', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getRelatedArtifacts = useCallback(async (sha256) => {
    try {
      setLoading(true);
      setError(null);
      setPivotType('related_artifacts');

      const response = await fetch(
        `${apiService.baseUrl}/pivots/related-artifacts/${encodeURIComponent(sha256)}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch related artifacts: ${response.status}`);
      }

      const data = await response.json();
      setPivotData(data);
      return data;
    } catch (err) {
      console.error('Error fetching related artifacts:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getIconHashStatistics = useCallback(async (iconHash) => {
    try {
      setLoading(true);
      setError(null);
      setPivotType('icon_hash_statistics');

      const response = await fetch(
        `${apiService.baseUrl}/pivots/icon-hash/${encodeURIComponent(iconHash)}/stats`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch statistics: ${response.status}`);
      }

      const data = await response.json();
      setPivotData(data);
      return data;
    } catch (err) {
      console.error('Error fetching icon hash statistics:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const pivotByArtifact = useCallback(async (artifactSha256, limit = 100) => {
    try {
      setLoading(true);
      setError(null);
      setPivotType('artifact_hash');

      const response = await fetch(
        `${apiService.baseUrl}/pivots/artifact/${encodeURIComponent(artifactSha256)}?limit=${limit}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to pivot: ${response.status}`);
      }

      const data = await response.json();
      setPivotData(data);
      return data;
    } catch (err) {
      console.error('Pivot error (artifact_hash):', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getCorrelatedSamples = useCallback(async (sha256, limit = 100) => {
    try {
      setLoading(true);
      setError(null);
      setPivotType('correlated_samples');

      const response = await fetch(
        `${apiService.baseUrl}/pivots/${encodeURIComponent(sha256)}/correlated-samples?limit=${limit}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch correlated samples: ${response.status}`);
      }

      const data = await response.json();
      setPivotData(data);
      return data;
    } catch (err) {
      console.error('Error fetching correlated samples:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getSharedArtifacts = useCallback(async (sha256) => {
    try {
      const response = await fetch(
        `${apiService.baseUrl}/pivots/${encodeURIComponent(sha256)}/shared-artifacts`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch shared artifacts: ${response.status}`);
      }

      return await response.json();
    } catch (err) {
      console.error('Error fetching shared artifacts:', err);
      return null;
    }
  }, []);

  const pivotByService = useCallback(async (serviceName, limit = 100, excludeSha256 = null) => {
    try {
      setLoading(true);
      setError(null);
      setPivotType('service');
      const data = await apiService.pivotByService(serviceName, limit, excludeSha256);
      setPivotData(data);
      return data;
    } catch (err) {
      console.error('Pivot error (service):', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const pivotByReceiver = useCallback(async (receiverName, limit = 100, excludeSha256 = null) => {
    try {
      setLoading(true);
      setError(null);
      setPivotType('receiver');
      const data = await apiService.pivotByReceiver(receiverName, limit, excludeSha256);
      setPivotData(data);
      return data;
    } catch (err) {
      console.error('Pivot error (receiver):', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const pivotByIntentAction = useCallback(async (intentAction, limit = 100, excludeSha256 = null) => {
    try {
      setLoading(true);
      setError(null);
      setPivotType('intent_action');
      const data = await apiService.pivotByIntentAction(intentAction, limit, excludeSha256);
      setPivotData(data);
      return data;
    } catch (err) {
      console.error('Pivot error (intent_action):', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const clearPivot = useCallback(() => {
    setPivotData(null);
    setError(null);
    setPivotType(null);
  }, []);

  return {
    pivotData,
    loading,
    error,
    pivotType,
    pivotByIconHash,
    pivotByCertificate,
    pivotByPermission,
    pivotByPermHash,
    pivotByMalwareFamily,
    getRelatedArtifacts,
    getIconHashStatistics,
    pivotByArtifact,
    getCorrelatedSamples,
    getSharedArtifacts,
    pivotByService,
    pivotByReceiver,
    pivotByIntentAction,
    clearPivot
  };
};